import { Box, Center, Container, Text, UnstyledButton } from '@mantine/core';
import { Helmet } from 'react-helmet';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'tabler-icons-react';
import Transactions from '../components/admin-transactions/admin-transactions.component';
import Logs from '../components/logs/logs.component';
import AddMids from '../components/mid/add-mid.component';
import ViewAllMids from '../components/mid/view-all-mids.component';
import CreateNewPos from '../components/pos/create-pos.component';
import ViewAllPOS from '../components/pos/view-all-pos.component';
import RequireAuth from '../components/require-auth/require-auth.component';
import { AccountTypes } from '../enums/account-type';
import { MerchantOnboardingStatus } from '../enums/merchant-onboarding-status';
import { Roles } from '../enums/roles-enum';
import AdminBranchesPage from '../pages/admin-pages/admin-branches.page';
import AdminMerchantsPage from '../pages/admin-pages/admin-merchants.page';
import AdminOnboardingPage from '../pages/admin-pages/admin-onboarding.page';
import AdminPartnersPage from '../pages/admin-pages/admin-partners.page';
import AdminResellersPage from '../pages/admin-pages/admin-resellers.page';
import SettlementsPage from '../pages/admin-pages/settlements-page';
import RefundApprovalPage from '../pages/auth-pages/refund-approval/refund-approval.page';
import MissingPage from '../pages/common-pages/missing.page';
import PaymentLinksPage from '../pages/common-pages/payment-links.page';
import UserManagement from '../components/user-management/user-management.component';

const AdminRoutes = () => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  let backLink;

  if (pathname.includes('merchants/') || pathname.includes('update')) {
    backLink = '/admin/merchants';
  }

  return (
    <>
      <Helmet>
        <title>XPAY.World - Admin Dashboard</title>
      </Helmet>
      <Container fluid px="xl">
        {backLink && (
          <UnstyledButton
            color="dimmed"
            onClick={() => {
              navigate(-1);
            }}
            mt="lg"
            data-cy="backButton"
          >
            <Center inline>
              <ArrowLeft size={12} color="#868e96" />
              <Box ml={5}>
                <Text size="sm" color="dimmed">
                  Back
                </Text>
              </Box>
            </Center>
          </UnstyledButton>
        )}

        <Routes>
          <Route path="/" />
          <Route
            element={
              <RequireAuth
                allowedRoles={[Roles.ADMINISTRATOR, Roles.SETTLEMENT]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="settlements" element={<SettlementsPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[Roles.ADMINISTRATOR, Roles.SETTLEMENT]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="logs" element={<Logs />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[
                  Roles.ADMINISTRATOR,
                  Roles.SETTLEMENT,
                  Roles.CHECKER
                ]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="partners" element={<AdminPartnersPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[
                  Roles.ADMINISTRATOR,
                  Roles.SETTLEMENT,
                  Roles.CHECKER
                ]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="resellers" element={<AdminResellersPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[
                  Roles.ADMINISTRATOR,
                  Roles.SETTLEMENT,
                  Roles.CHECKER
                ]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="merchants" element={<AdminMerchantsPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[
                  Roles.ADMINISTRATOR,
                  Roles.SETTLEMENT,
                  Roles.CHECKER
                ]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="branches" element={<AdminBranchesPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[Roles.ADMINISTRATOR]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="create-payment-links" element={<PaymentLinksPage />} />
          </Route>

          <Route path="onboarding">
            <Route index element={<AdminOnboardingPage />} />
          </Route>

          <Route
            element={
              <RequireAuth
                allowedRoles={[Roles.ADMINISTRATOR, Roles.SETTLEMENT]}
                allowedAccountType={Object.values(AccountTypes)}
                allowedOnboardingStatus={Object.values(
                  MerchantOnboardingStatus
                )}
              />
            }
          >
            <Route path="transactions">
              <Route index element={<Transactions isAdmin={true} />} />
            </Route>
          </Route>

          <Route path="mids">
            <Route index element={<ViewAllMids />} />
            <Route
              element={
                <RequireAuth
                  allowedRoles={[Roles.ADMINISTRATOR]}
                  allowedAccountType={Object.values(AccountTypes)}
                  allowedOnboardingStatus={Object.values(
                    MerchantOnboardingStatus
                  )}
                />
              }
            >
              <Route path="add" element={<AddMids />} />
            </Route>
          </Route>

          <Route path="users">
            <Route index element={<UserManagement />} />
          </Route>

          <Route path="refund">
            <Route index element={<RefundApprovalPage />} />
          </Route>

          <Route path="pos">
            <Route index element={<ViewAllPOS />} />
            <Route
              element={
                <RequireAuth
                  allowedRoles={[Roles.ADMINISTRATOR]}
                  allowedAccountType={Object.values(AccountTypes)}
                  allowedOnboardingStatus={Object.values(
                    MerchantOnboardingStatus
                  )}
                />
              }
            >
              <Route path="create" element={<CreateNewPos />} />
            </Route>
          </Route>

          <Route path="*" element={<MissingPage />} />
        </Routes>
      </Container>
    </>
  );
};

export default AdminRoutes;
